import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PullRequestStateIcons} from '@github-ui/issue-pr-state/Icons'
import {LabelToken} from '@github-ui/label-token'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {useAnalytics} from '@github-ui/use-analytics'
import {CommentIcon} from '@primer/octicons-react'
import {AvatarStack} from '@primer/react'
import {Tooltip} from '@primer/react/deprecated'
import {SkeletonBox} from '@primer/react/experimental'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'
import styles from './PullsListItem.module.css'
import unreadStyles from '../shared/UnreadBorder.module.css'
import type {EnrichedPull} from './types/list-types'
import {PullsListItemTimestampContainer} from './PullsListItemTimestampContainer'
import {
  reviewDecision,
  reviewDecisionCompact,
  statusCheckDisplay,
  statusCheckDisplayCompact,
} from './helpers/list-helpers'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {PullsChecksStatusBadge} from './PullsChecksStatusBadge'
import {StackBadge} from '@github-ui/stack-badge'
import {deriveAttributedUsername, isAgentAuthor} from './helpers/derive-attributed-username'
import {PR_STATE_FALLBACK_TOOLTIP_LABEL, PR_STATE_TOOLTIP_LABELS} from './consts/pr-state-consts'

interface PullsListItemProps {
  pull: EnrichedPull
  onFilterSelect?: (type: 'repo' | 'author' | 'label' | 'assignee', value: string) => void
  isDeferredLoading?: boolean
  isAgentAttributionEnabled?: boolean
}

function renderCompactMetadataValue(value: ReactNode) {
  return value === null || value === undefined || value === '' ? (
    <span className={styles.emptyPlaceholder}>&mdash;</span>
  ) : (
    value
  )
}

export function PullsListItem({
  pull,
  onFilterSelect,
  isDeferredLoading,
  isAgentAttributionEnabled,
}: PullsListItemProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {variant} = useListViewVariant()
  const isCompact = variant === 'compact'
  const {isBrowser} = useRuntimeEnvironment()

  const commitChecksDialogEnabled = isBrowser && isFeatureEnabled('pull_request_commit_checks_dialog')
  const attributedUsername = isAgentAttributionEnabled ? deriveAttributedUsername(pull) : undefined
  const isPullAgentAuthored = isAgentAttributionEnabled ? isAgentAuthor(pull) : false

  // `displayState` is optional in the payload (the backend derives it from a
  // nilable `PullRequest::Icon::State`), so guard the icon lookup: an unknown or
  // missing state would otherwise resolve to `undefined` and crash the row when
  // rendered. Fall back to the neutral open pull-request glyph, pairing with the
  // existing `PR_STATE_FALLBACK_TOOLTIP_LABEL`.
  const PRStateIcon =
    PullRequestStateIcons[pull.displayState as keyof typeof PullRequestStateIcons] ?? PullRequestStateIcons.OPEN
  const prStateTooltipLabel =
    PR_STATE_TOOLTIP_LABELS[pull.displayState as keyof typeof PullRequestStateIcons] ?? PR_STATE_FALLBACK_TOOLTIP_LABEL
  const [owner, repo] = pull.repoNameWithOwner.split('/')
  const hovercardAttributes = getHovercardAttributesForType('pull_request', {
    owner,
    repo,
    pullRequestNumber: pull.number,
  })
  const onTitleClick = () =>
    sendAnalyticsEvent('pull-requests.click', 'LIST_ITEM_TITLE', {displayState: pull.displayState})
  const labelBadges = !isCompact
    ? pull.labels.map(label => {
        const token = (
          <LabelToken
            text={label.name}
            fillColor={label.color ? `#${label.color}` : undefined}
            style={{marginRight: '4px', cursor: onFilterSelect ? 'pointer' : undefined}}
          />
        )

        if (!onFilterSelect) {
          return <span key={label.id}>{token}</span>
        }

        return (
          <button
            key={label.id}
            type="button"
            className={styles.filterLink}
            onClick={() => onFilterSelect('label', label.name)}
            aria-label={`Filter by label ${label.name}`}
          >
            {token}
          </button>
        )
      })
    : undefined

  const compactRepoName = onFilterSelect ? (
    <button
      type="button"
      className={styles.filterLink}
      onClick={() => onFilterSelect('repo', pull.repoNameWithOwner)}
      data-testid="repo-filter-link"
      aria-label={`Filter by repository ${pull.repoNameWithOwner}`}
    >
      {pull.repoNameWithOwner}
    </button>
  ) : (
    <span>{pull.repoNameWithOwner}</span>
  )

  const renderAuthorLink = (login: string, testId: string) =>
    onFilterSelect ? (
      <button
        type="button"
        className={styles.filterLink}
        onClick={() => onFilterSelect('author', login)}
        data-testid={testId}
        aria-label={`Filter by author ${login}`}
      >
        {login}
      </button>
    ) : (
      <span>{login}</span>
    )

  const compactAuthor = attributedUsername ? (
    <>
      {renderAuthorLink(attributedUsername, 'attributed-author-filter-link')}
      <span> with </span>
      {renderAuthorLink(pull.author.displayLogin, 'author-filter-link')}
    </>
  ) : (
    renderAuthorLink(pull.author.displayLogin, 'author-filter-link')
  )

  const compactStatus = pull.statusCheckRollup ? (
    commitChecksDialogEnabled && pull.headSha ? (
      <PullsChecksStatusBadge
        headSha={pull.headSha}
        repositoryNwo={pull.repoNameWithOwner}
        statusCheckRollup={pull.statusCheckRollup}
        aliveChannel={pull.commitHeadShaChannel}
      />
    ) : (
      statusCheckDisplayCompact(pull.statusCheckRollup)
    )
  ) : null

  const metadata = isCompact ? (
    <>
      <ListItemMetadata className={clsx(styles.compactMetadataCell, styles.compactMetadataReview)}>
        {renderCompactMetadataValue(
          pull.reviewDecisionState
            ? reviewDecisionCompact(pull.reviewDecisionState, {
                approvedReviewsCount: pull.approvedReviewsCount,
                changesRequestedReviewsCount: pull.changesRequestedReviewsCount,
              })
            : null,
        )}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(styles.compactMetadataCell, styles.compactMetadataStatus)}>
        {renderCompactMetadataValue(compactStatus)}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(styles.compactMetadataCell, styles.compactMetadataComments)}>
        {renderCompactMetadataValue(
          pull.commentCount > 0 ? (
            <>
              <CommentIcon aria-label={`${pull.commentCount} comments`} size={16} /> {pull.commentCount}
            </>
          ) : null,
        )}
      </ListItemMetadata>
      <ListItemMetadata className={clsx(styles.compactMetadataCell, styles.compactMetadataAssignees)}>
        {renderCompactMetadataValue(
          pull.assignees && pull.assignees.length > 0 ? (
            <AvatarStack>
              {pull.assignees.map(assignee =>
                onFilterSelect ? (
                  <button
                    key={assignee.login}
                    type="button"
                    className={styles.assigneeFilterButton}
                    onClick={() => onFilterSelect('assignee', assignee.login)}
                    aria-label={`Filter by assignee ${assignee.login}`}
                    {...getHovercardAttributesForType('user', {login: assignee.login})}
                  >
                    <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
                  </button>
                ) : (
                  <GitHubAvatar
                    key={assignee.login}
                    src={assignee.avatarUrl}
                    size={20}
                    alt={assignee.login}
                    {...getHovercardAttributesForType('user', {login: assignee.login})}
                  />
                ),
              )}
            </AvatarStack>
          ) : null,
        )}
      </ListItemMetadata>
    </>
  ) : (
    <>
      {pull.commentCount > 0 ? (
        <ListItemMetadata>
          <CommentIcon size={16} /> {pull.commentCount}
        </ListItemMetadata>
      ) : null}
      {pull.assignees && pull.assignees.length > 0 && (
        <ListItemMetadata alignment="right">
          <AvatarStack alignRight>
            {pull.assignees.map(assignee =>
              onFilterSelect ? (
                <button
                  key={assignee.login}
                  type="button"
                  className={styles.assigneeFilterButton}
                  onClick={() => onFilterSelect('assignee', assignee.login)}
                  aria-label={`Filter by assignee ${assignee.login}`}
                  {...getHovercardAttributesForType('user', {login: assignee.login})}
                >
                  <GitHubAvatar src={assignee.avatarUrl} size={20} alt="" />
                </button>
              ) : (
                <GitHubAvatar
                  key={assignee.login}
                  src={assignee.avatarUrl}
                  size={20}
                  alt={assignee.login}
                  {...getHovercardAttributesForType('user', {login: assignee.login})}
                />
              ),
            )}
          </AvatarStack>
        </ListItemMetadata>
      )}
    </>
  )

  return (
    <ListItem
      key={pull.id}
      className={clsx(
        isCompact ? styles.listItemCompact : styles.listItem,
        !pull.isReadByCurrentUser && unreadStyles.unreadBorder,
      )}
      title={
        <ListItemTitle
          href={pull.permalink}
          onClick={onTitleClick}
          value={pull.titleHtml ? <UnsafeHTMLText as="span" html={pull.titleHtml} /> : <span>{pull.title}</span>}
          linkProps={{...hovercardAttributes, 'data-testid': 'listitem-title-link'}}
          anchorClassName={styles.titleLink}
          trailingBadges={labelBadges}
        />
      }
      metadata={metadata}
      metadataContainerClassName={isCompact ? styles.compactMetadataContainer : undefined}
    >
      <ListItemLeadingContent>
        {!pull.isReadByCurrentUser && <span className="sr-only">Unread</span>}
        <ListItemLeadingVisual>
          <Tooltip text={prStateTooltipLabel} direction="e">
            <PRStateIcon aria-label={prStateTooltipLabel} />
          </Tooltip>
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        {isCompact ? (
          <ListItemDescription className={styles.compactLeftMeta}>
            <span className={styles.compactLeftMetaText}>{compactRepoName}</span>
            <span className={styles.compactLeftMetaSeparator} aria-hidden="true">
              •
            </span>
            <span className={styles.compactLeftMetaText}>{compactAuthor}</span>
          </ListItemDescription>
        ) : (
          <ListItemDescription className={styles.description}>
            <span>
              {compactRepoName}
              <span>#{pull.number} </span>
            </span>
            <PullsListItemTimestampContainer
              createdAt={new Date(pull.createdAt)}
              updatedAt={new Date(pull.updatedAt)}
              prState={pull.state}
              author={pull.author.displayLogin}
              isAgent={isPullAgentAuthored}
              attributedUsername={attributedUsername}
              onAuthorClick={onFilterSelect ? author => onFilterSelect('author', author) : undefined}
            />
            <StackBadge
              pullRequestNumber={pull.number}
              stackPosition={pull.stackPosition}
              stackSize={pull.stackSize}
              repoNameWithOwner={pull.repoNameWithOwner}
            />
            {pull.reviewDecisionState ? (
              reviewDecision(pull.reviewDecisionState, {
                approvedReviewsCount: pull.approvedReviewsCount,
                changesRequestedReviewsCount: pull.changesRequestedReviewsCount,
              })
            ) : isDeferredLoading ? (
              <SkeletonBox className={styles.inlineSkeleton} width="100px" />
            ) : null}
            {pull.statusCheckRollup ? (
              commitChecksDialogEnabled && pull.headSha ? (
                <span className={styles.inlineChecksBadge}>
                  <PullsChecksStatusBadge
                    headSha={pull.headSha}
                    repositoryNwo={pull.repoNameWithOwner}
                    statusCheckRollup={pull.statusCheckRollup}
                    aliveChannel={pull.commitHeadShaChannel}
                  />
                </span>
              ) : (
                statusCheckDisplay(pull.statusCheckRollup)
              )
            ) : isDeferredLoading ? (
              <SkeletonBox className={styles.inlineSkeleton} height="1lh" width="48px" />
            ) : null}
          </ListItemDescription>
        )}
      </ListItemMainContent>
    </ListItem>
  )
}
